import { useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { Button, Card, TextField } from "@atlas/ui/web";
import { atlas } from "../../lib/sdk";
import { AtlasLogo } from "../../components/AtlasBrand";

/**
 * Troca OBRIGATORIA da senha no primeiro acesso do sub-usuario da organizacao.
 *
 * Quem cria o sub-usuario e' o admin, e e' ele quem escolhe a senha inicial —
 * entao ela e' conhecida por duas pessoas. Esta tela existe para que deixe de
 * ser: enquanto a troca nao acontece, `prefeitura_senha_temporaria` vem `true` no
 * login e o `finalize()` da tela de login manda para ca' em vez do painel.
 *
 * Nao e' um portao de seguranca — o token JA e' valido quando se chega aqui, e um
 * cliente que ignore a tela entraria do mesmo jeito. O que protege de verdade e'
 * o `assertSenhaTrocadaPref` nas rotas da API. Espelha `banco/trocar-senha.tsx`.
 */
export function PrefeituraTrocarSenha() {
  const nav = useNavigate();
  const [senhaAtual, setSenhaAtual] = useState("");
  const [nova, setNova] = useState("");
  const [nova2, setNova2] = useState("");
  const [erro, setErro] = useState<string | null>(null);

  const trocar = useMutation({
    mutationFn: async () => {
      await atlas.prefeitura.trocarMinhaSenha(senhaAtual, nova);
      // RENOVA a sessao antes de sair daqui. A troca muda o CADASTRO, nao o
      // token: o access token em maos continua carregando
      // `prefeitura_senha_temporaria: true`, e o layout — que le a claim —
      // devolveria a pessoa para esta mesma tela. `/v1/auth/refresh` re-deriva a
      // claim do registro atual.
      await atlas.renovarSessao();
    },
    onSuccess: () => nav("/prefeitura/dashboard", { replace: true }),
    onError: (e) => setErro(e instanceof Error ? e.message : "Não foi possível trocar a senha."),
  });

  function submit(e: FormEvent) {
    e.preventDefault();
    setErro(null);
    if (nova.length < 6) { setErro("A nova senha deve ter no mínimo 6 caracteres."); return; }
    if (nova !== nova2) { setErro("As senhas não conferem."); return; }
    if (nova === senhaAtual) { setErro("A nova senha precisa ser diferente da temporária."); return; }
    trocar.mutate();
  }

  return (
    <div className="auth-shell">
      <Card style={{ background: "var(--surface)", maxWidth: 480 }}>
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 10, marginBottom: 20, textAlign: "center" }}>
          <AtlasLogo height={72} />
          <div>
            <div style={{ fontWeight: 700, fontSize: "1.1rem" }}>Crie sua senha</div>
            <div style={{ fontSize: ".82rem", color: "var(--text-dim)" }}>
              A senha atual foi definida pelo administrador do seu órgão. Escolha uma que só você saiba.
            </div>
          </div>
        </div>

        <form onSubmit={submit} style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <TextField
            label="Senha temporária"
            type="password"
            value={senhaAtual}
            onChange={(e) => setSenhaAtual(e.target.value)}
            autoComplete="current-password"
            required
            autoFocus
          />
          <TextField
            label="Nova senha"
            type="password"
            value={nova}
            onChange={(e) => setNova(e.target.value)}
            autoComplete="new-password"
            hint="Mínimo 6 caracteres."
            required
          />
          <TextField
            label="Confirmar nova senha"
            type="password"
            value={nova2}
            onChange={(e) => setNova2(e.target.value)}
            autoComplete="new-password"
            required
          />
          {erro ? (
            <div
              style={{
                padding: "12px 14px",
                borderRadius: 10,
                border: "1px solid var(--danger-500)",
                background: "color-mix(in srgb, var(--danger-500) 10%, transparent)",
                color: "var(--text)",
                fontSize: ".88rem",
              }}
            >
              {erro}
            </div>
          ) : null}
          <Button type="submit" disabled={trocar.isPending}>
            {trocar.isPending ? "Salvando..." : "Salvar e entrar →"}
          </Button>
        </form>
      </Card>
    </div>
  );
}
